import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, List, Tag, Button, Modal, message } from 'antd';
import ProForm, {
  ModalForm,
  ProFormText,
  ProFormSelect,
} from '@ant-design/pro-form';
import { PlusOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import {
  ADD_DEPARTMENT,
  DELETE_DEPARTMENT,
  GET_FACULTIES,
  TOGGLE_DEPARTMENT_DISABLED,
} from 'gql/faculties';
import { PLACEHOLDER } from 'constants/form';

const DeparementsList = ({ departments }) => {
  const { id } = useParams();
  const [
    toggleDepartmentDisabled,
    { loading: toggleDepartmentDisabledLoading },
  ] = useMutation(TOGGLE_DEPARTMENT_DISABLED, {
    refetchQueries: [{ query: GET_FACULTIES, variables: { ids: [id] } }],
  });
  const [deleteDepartment, { loading: deleteDepartmentLoading }] = useMutation(
    DELETE_DEPARTMENT,
    {
      refetchQueries: [{ query: GET_FACULTIES, variables: { ids: [id] } }],
    }
  );
  const [addDepartment, { loading: addDepartmentLoading }] = useMutation(
    ADD_DEPARTMENT,
    {
      refetchQueries: [{ query: GET_FACULTIES, variables: { ids: [id] } }],
    }
  );

  const extra = (
    <ModalForm
      title="Add Department"
      trigger={
        <Button type="link">
          <PlusOutlined />
          Add
        </Button>
      }
      submitter={{
        searchConfig: {
          resetText: 'Cancel',
          submitText: 'Submit',
        },
        submitButtonProps: {
          loading: addDepartmentLoading,
        },
      }}
      onFinish={async (input) => {
        await addDepartment({
          variables: {
            addDepartmentInput: { faculty: id, ...input },
          },
        });
        message.success(`${input.abbreviation} successfully added`);
        return true;
      }}
    >
      <ProForm.Group>
        <ProFormText
          width="md"
          name="name"
          label="Name"
          placeholder={PLACEHOLDER.PLEASE_SELECT}
          rules={[
            {
              required: true,
              message: 'Please enter department name!',
            },
          ]}
        />
        <ProFormText
          width="md"
          name="abbreviation"
          label="Abbreviation"
          rules={[
            {
              required: true,
              message: 'Please enter department abbreviation!',
            },
          ]}
          placeholder={PLACEHOLDER.PLEASE_SELECT}
        />
        <ProFormSelect
          width="md"
          name="controlType"
          label="Control Type"
          placeholder={PLACEHOLDER.PLEASE_SELECT}
          rules={[
            {
              required: true,
              message: 'Please select control type!',
            },
          ]}
          valueEnum={{
            DEPARTMENT: 'DEPARTMENT',
            FACULTY: 'FACULTY',
            SCHOOL: 'SCHOOL',
            NAU: 'NAU',
          }}
        />
      </ProForm.Group>
    </ModalForm>
  );

  const listActions = ({ item }) => {
    return [
      <Link key="edit" to={`/system-setting/faculties/departments/${item.id}`}>
        Edit
      </Link>,
      <Button
        type="link"
        key="state"
        loading={toggleDepartmentDisabledLoading}
        onClick={async () => {
          await toggleDepartmentDisabled({
            variables: {
              id: item.id,
            },
          });
        }}
      >
        {item.disabled ? 'Active' : 'Disable'}
      </Button>,
      <Button
        type="link"
        key={item.id}
        onClick={() => {
          Modal.confirm({
            title: `${item.abbreviation} (${item.name})`,
            icon: <ExclamationCircleOutlined />,
            content: 'Confirm to delete this Department?',
            cancelButtonProps: {
              loading: deleteDepartmentLoading,
            },
            okButtonProps: {
              loading: deleteDepartmentLoading,
            },
            okText: 'Confirm',
            cancelText: 'Cancel',
            onOk: async () => {
              await deleteDepartment({
                variables: {
                  id: item.id,
                },
              });
              message.success(`${item.abbreviation} successfully deleted`);
            },
          });
        }}
      >
        Delete{' '}
      </Button>,
    ];
  };

  return (
    <Card title="Departments" bordered extra={extra}>
      <List
        itemLayout="horizontal"
        dataSource={departments}
        renderItem={(item) =>
          item.deleted ? (
            ''
          ) : (
            <List.Item actions={listActions({ item })}>
              <List.Item.Meta
                title={item.abbreviation}
                description={
                  <>
                    {item.controlType}
                    <br />
                    {item.name}
                  </>
                }
              />
              {item.disabled ? (
                <Tag>Disabled</Tag>
              ) : item.deleted ? (
                <Tag color="red">Deleted</Tag>
              ) : (
                <Tag color="green">Active</Tag>
              )}
            </List.Item>
          )
        }
      ></List>
    </Card>
  );
};

DeparementsList.propTypes = {
  departments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      controlType: PropTypes.string.isRequired,
      abbreviation: PropTypes.string.isRequired,
      disabled: PropTypes.bool.isRequired,
      deleted: PropTypes.bool.isRequired,
    })
  ),
};

export default DeparementsList;
